import React, { useEffect, useState } from "react";
import api, { formatApiError, formatCurrency } from "../lib/api";
import { PageHeader } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { LogoUploader } from "../components/LogoUploader";
import { TaxesEditor, PaymentFeesEditor, LossField } from "../components/TaxFeeEditors";
import { ChangePasswordCard } from "../components/ChangePasswordCard";
import { TeamCard } from "../components/Team";
import { ProfessionPicker } from "../components/ProfessionPicker";
import { getProfession } from "../lib/professions";
import { UnitsCard } from "../components/Units";

export default function SettingsPage() {
  const { user, setUser } = useAuth();
  const [settings, setSettings] = useState(null);
  const [hourCost, setHourCost] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/settings"), api.get("/dashboard")])
      .then(([s, d]) => { setSettings(s.data); setHourCost(d.data.hour_cost); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const update = (k) => (e) => setSettings({ ...settings, [k]: e.target.value });
  const prof = getProfession(user?.profession);

  const changeProfession = async (profession) => {
    try {
      await api.put("/auth/profession", { profession });
      setUser({ ...user, profession });
      toast.success(`Área alterada para ${getProfession(profession).label}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        clinic_name: settings.clinic_name || "",
        monthly_clinical_hours: parseFloat(settings.monthly_clinical_hours),
        desired_profit_margin: parseFloat(settings.desired_profit_margin),
        minimum_profit_margin: parseFloat(settings.minimum_profit_margin),
        approximate_profit_margin: parseFloat(settings.approximate_profit_margin),
        tax_percentage: parseFloat(settings.tax_percentage),
        card_fee_percentage: parseFloat(settings.card_fee_percentage),
        taxes: (settings.taxes || []).filter((t) => t.name.trim()).map((t) => ({ name: t.name.trim(), percentage: parseFloat(t.percentage) || 0 })),
        payment_fees: (settings.payment_fees || []).filter((f) => f.name.trim()).map((f) => ({ name: f.name.trim(), percentage: parseFloat(f.percentage) || 0, is_default: !!f.is_default })),
        default_loss_percentage: parseFloat(settings.default_loss_percentage) || 0,
        goal_revenue_month: parseFloat(settings.goal_revenue_month) || 0,
        goal_quotes_month: parseInt(settings.goal_quotes_month) || 0,
      };
      await api.put("/settings", payload);
      if (user) setUser({ ...user, clinic_name: payload.clinic_name });
      toast.success("Configurações salvas!");
      const d = await api.get("/dashboard");
      setHourCost(d.data.hour_cost);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="text-slate-500">Carregando...</div>;

  return (
    <div data-testid="settings-page">
      <PageHeader title="Configurações" subtitle={`Defina a hora de atendimento, margens de lucro, impostos e taxas ${prof.de} sua ${prof.place}.${user?.unit ? ` Unidade ativa: ${user.unit.name}.` : ""}`} />

      {hourCost && (
        <div className="card-crisp p-5 mb-6 bg-gradient-to-br from-indigo-50 to-white">
          <div className="text-xs font-medium uppercase tracking-wider text-indigo-700 mb-1">Hora clínica atual</div>
          <div className="font-heading font-extrabold text-3xl text-slate-900 tabular-nums">{formatCurrency(hourCost.clinical_hour_cost)}</div>
          <div className="text-sm text-slate-600 mt-1">
            Overhead mensal {formatCurrency(hourCost.monthly_overhead)} ÷ {hourCost.monthly_clinical_hours}h = custo da sua hora de operação.
          </div>
        </div>
      )}

      <div className="card-crisp p-6 space-y-6">
        {!user?.team_member && (
          <div data-testid="profession-section">
            <h3 className="font-heading font-semibold text-lg mb-1">Área de atuação</h3>
            <p className="text-xs text-slate-500 mb-3">Define o ícone, as cores e os termos usados no sistema. Atual: <strong>{getProfession(user?.profession).label}</strong>.</p>
            <ProfessionPicker compact value={user?.profession || "dentista"} onChange={changeProfession} />
          </div>
        )}
        <div>
          <h3 className="font-heading font-semibold text-lg mb-3">Dados {prof.de} {prof.place}</h3>
          <div>
            <Label>Nome {prof.de} {prof.place}</Label>
            <Input value={settings.clinic_name || ""} onChange={update("clinic_name")} data-testid="settings-clinic-name" className="mt-1" />
          </div>
          <LogoUploader hasLogo={!!settings.logo_path} onChange={(has) => setSettings({ ...settings, logo_path: has ? "set" : null })} />
        </div>

        <div>
          <h3 className="font-heading font-semibold text-lg mb-3">Hora clínica</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Horas clínicas trabalhadas por mês</Label>
              <Input type="number" step="1" value={settings.monthly_clinical_hours} onChange={update("monthly_clinical_hours")} data-testid="settings-hours" className="mt-1" />
              <p className="text-xs text-slate-500 mt-1">Ex: 8h/dia × 20 dias = 160h.</p>
            </div>
          </div>
        </div>

        <div>
          <h3 className="font-heading font-semibold text-lg mb-3">Margens de lucro</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label>Mínima (%)</Label>
              <Input type="number" step="0.1" value={settings.minimum_profit_margin} onChange={update("minimum_profit_margin")} data-testid="settings-margin-min" className="mt-1" />
            </div>
            <div>
              <Label>Aproximada (%)</Label>
              <Input type="number" step="0.1" value={settings.approximate_profit_margin} onChange={update("approximate_profit_margin")} data-testid="settings-margin-approx" className="mt-1" />
            </div>
            <div>
              <Label>Ideal (%)</Label>
              <Input type="number" step="0.1" value={settings.desired_profit_margin} onChange={update("desired_profit_margin")} data-testid="settings-margin-ideal" className="mt-1" />
            </div>
          </div>
        </div>

        <TaxesEditor items={settings.taxes || []} onChange={(taxes) => setSettings({ ...settings, taxes })} />
        <PaymentFeesEditor items={settings.payment_fees || []} onChange={(payment_fees) => setSettings({ ...settings, payment_fees })} />
        <LossField value={settings.default_loss_percentage ?? 0} onChange={update("default_loss_percentage")} />
        <div data-testid="goals-section">
          <h3 className="font-heading font-semibold text-lg mb-1">Metas mensais</h3>
          <p className="text-xs text-slate-500 mb-3">Acompanhadas no Dashboard (planos Master e Clínica). Contam orçamentos marcados como Aprovados no mês.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>Meta de faturamento aprovado (R$)</Label><Input type="number" step="100" value={settings.goal_revenue_month ?? 0} onChange={update("goal_revenue_month")} className="mt-1" data-testid="settings-goal-revenue" /></div>
            <div><Label>Meta de orçamentos aprovados</Label><Input type="number" step="1" value={settings.goal_quotes_month ?? 0} onChange={update("goal_quotes_month")} className="mt-1" data-testid="settings-goal-quotes" /></div>
          </div>
        </div>

        <div className="pt-4 border-t border-slate-200">
          <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="settings-save">
            {saving ? "Salvando..." : "Salvar alterações"}
          </Button>
        </div>
      </div>
      {!user?.team_member && <div className="mt-6"><UnitsCard /></div>}
      <div className="mt-6"><TeamCard /></div>
      <div className="mt-6"><ChangePasswordCard /></div>
    </div>
  );
}
